// Helper function to safely convert values to string
const safeStringify = (value) => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  
  if (typeof value === 'object') {
    if (value === null) return 'null'; // Double check for null
    
    try {
      // Handle arrays
      if (Array.isArray(value)) {
        return JSON.stringify(value);
      }
      
      // Handle regular objects
      return JSON.stringify(value);
    } catch (error) {
      // Fallback for circular references or other JSON.stringify errors
      try {
        return Object.prototype.toString.call(value);
      } catch (innerError) {
        return '[Object]';
      }
    }
  }
  
  // Fallback for any other type
  try {
    return String(value);
  } catch (error) {
    return '[Unknown]';
  }
};

// Helper function to validate and normalize data
const normalizeData = (data) => {
  // Handle null or undefined data
  if (data === null || data === undefined) {
    return [];
  }

  // Handle empty arrays
  if (Array.isArray(data) && data.length === 0) {
    return [];
  }

  // Convert single object to array
  if (!Array.isArray(data)) {
    // Handle primitive values
    if (typeof data !== 'object' || data === null) {
      return [{ value: data }];
    }
    
    // Handle objects - make sure it's a valid object before processing
    try {
      // Check if object has any enumerable properties
      const keys = Object.keys(data);
      if (keys.length === 0) {
        return [];
      }
      return [data];
    } catch (error) {
      // If Object.keys fails, treat as primitive
      return [{ value: data }];
    }
  }

  // Filter out null/undefined items from array and ensure objects
  return data.filter(item => item !== null && item !== undefined)
             .map(item => {
               if (typeof item !== 'object' || item === null) {
                 return { value: item };
               }
               
               // Additional safety check for objects
               try {
                 Object.keys(item); // Test if we can get keys
                 return item;
               } catch (error) {
                 return { value: item };
               }
             });
};

// Helper function to get all unique headers from data
const getAllHeaders = (rows) => {
  if (!rows || rows.length === 0) return [];
  
  const headerSet = new Set();
  rows.forEach(row => {
    if (row && typeof row === 'object' && row !== null) {
      try {
        Object.keys(row).forEach(key => headerSet.add(key));
      } catch (error) {
        // If Object.keys fails, skip this row
        console.warn('Failed to get keys from row:', row, error);
      }
    }
  });
  
  return Array.from(headerSet);
};

export default function JsonTable({ data, darkMode = true }) {
  // Normalize and validate data
  const rows = normalizeData(data);

  // Handle empty data
  if (rows.length === 0) {
    return (
      <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <p>No data available to display</p>
      </div>
    );
  }

  // Get all unique headers from all rows
  const headers = getAllHeaders(rows);

  // Handle case where no valid headers found
  if (headers.length === 0) {
    return (
      <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <p>No valid data structure found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table
        className={`relative table-auto w-full text-base text-left border overflow-x-auto max-h-1/2 ${darkMode ? 'text-white border-white/20' : 'text-black border-black/20'}`}
      >
        <thead className={darkMode ? 'bg-black text-white sticky -top-5' : 'bg-gray-200 text-black'}>
          <tr>
            {headers.map((header) => (
              <th
                key={header}
                className={`px-4 py-2 border-b ${darkMode ? 'border-white/20' : 'border-black/20'}`}
              >
                {safeStringify(header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={darkMode ? 'border-t border-white/10' : 'border-t border-black/10'}
            >
              {headers.map((key) => (
                <td key={key} className="px-4 py-2">
                  {safeStringify(row?.[key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const PDFJsonTable = ({ data }) => {
  // Normalize and validate data
  const rows = normalizeData(data);

  // Handle empty data
  if (rows.length === 0) {
    return (
      <div className="p-4 text-center text-gray-600">
        <p>No data available to display</p>
      </div>
    );
  }

  // Get all unique headers from all rows
  const headers = getAllHeaders(rows);

  // Handle case where no valid headers found
  if (headers.length === 0) {
    return (
      <div className="p-4 text-center text-gray-600">
        <p>No valid data structure found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto mt-4">
      <table
        className="table-auto w-full text-base text-left border"
        style={{ borderColor: '#e2e8f0' }}
      >
        <thead style={{ backgroundColor: '#e2e8f0', color: 'black' }}>
          <tr>
            {headers.map((header) => (
              <th key={header} className="px-4 py-2 border-b" style={{ borderColor: '#e2e8f0' }}>
                {safeStringify(header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t" style={{ borderColor: '#e2e8f0' }}>
              {headers.map((key) => (
                <td key={key} className="px-4 py-2" style={{ borderColor: '#e2e8f0' }}>
                  {safeStringify(row?.[key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};